import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import PageLoader from '../../components/PageLoader';
import TablePagination from '../../components/TablePagination';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiEdit, FiTrash2, FiDollarSign, FiKey, FiServer, FiRepeat, FiX } from 'react-icons/fi';
import { admin, roleBadgeClass, statusBadgeClass } from '../../components/admin/adminUi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PAGE_SIZE = 15;
const token = () => localStorage.getItem('token');
const authH = () => ({ headers: { Authorization: `Bearer ${token()}` } });

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [serverCounts, setServerCounts] = useState({});
  const [serversModal, setServersModal] = useState(null);
  const [userServers, setUserServers] = useState([]);
  const [loadingServers, setLoadingServers] = useState(false);
  const [transferModal, setTransferModal] = useState(null);
  const [transferUserId, setTransferUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const totalPages = Math.max(1, Math.ceil(users.length / PAGE_SIZE));
  useEffect(() => { setPage(p => p > totalPages ? 1 : p); }, [users.length, totalPages]);
  const pagedUsers = useMemo(() => users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [users, page]);

  const filteredUsersForTransfer = useMemo(() => {
    if (!userSearch.trim()) return users;
    const q = userSearch.toLowerCase();
    return users.filter(u => u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
  }, [users, userSearch]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/users`, authH());
      setUsers(res.data.users || []);
    } catch { toast.error('Failed to fetch users'); }
    finally { setLoading(false); }
  };

  const fetchServerCounts = async (allUsers) => {
    try {
      const res = await axios.get(`${API_URL}/admin/servers`, authH());
      const servers = res.data.servers || [];
      const counts = {};
      for (const s of servers) {
        const uid = s.userId?._id || s.userId;
        if (uid) counts[uid] = (counts[uid] || 0) + 1;
      }
      setServerCounts(counts);
    } catch { /* silent */ }
  };

  useEffect(() => { if (users.length) fetchServerCounts(users); }, [users]);

  const fetchUserServers = async (userId) => {
    setLoadingServers(true);
    try {
      const res = await axios.get(`${API_URL}/admin/users/${userId}/servers`, authH());
      setUserServers(res.data.servers || []);
    } catch { toast.error('Failed to fetch user servers'); }
    finally { setLoadingServers(false); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, authH());
      toast.success('User deleted successfully');
      fetchUsers();
    } catch { toast.error('Failed to delete user'); }
  };

  const handleBalanceUpdate = async (userId) => {
    if (!balanceAmount) { toast.error('Please enter an amount'); return; }
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/balance`, { amount: parseFloat(balanceAmount) }, authH());
      toast.success('Balance updated successfully');
      setEditingUser(null);
      setBalanceAmount('');
      fetchUsers();
    } catch { toast.error('Failed to update balance'); }
  };

  const handlePasswordUpdate = async (userId) => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/password`, { newPassword }, authH());
      toast.success('Password updated successfully');
      setEditingUser(null);
      setNewPassword('');
    } catch { toast.error('Failed to update password'); }
  };

  const openServersModal = (user) => {
    setServersModal(user);
    fetchUserServers(user._id);
  };

  const handleDeleteServer = async (serverId) => {
    if (!window.confirm('Delete this server?')) return;
    try {
      await axios.delete(`${API_URL}/admin/servers/${serverId}`, authH());
      toast.success('Server deleted');
      fetchUserServers(serversModal._id);
      fetchServerCounts(users);
    } catch { toast.error('Failed to delete server'); }
  };

  const openTransferFromUser = (server) => {
    setTransferModal(server);
    setTransferUserId('');
    setUserSearch('');
  };

  const handleTransfer = async () => {
    if (!transferUserId) { toast.error('Select a user'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/servers/${transferModal._id}/transfer`, { newUserId: transferUserId }, authH());
      toast.success('Server transferred');
      setTransferModal(null);
      fetchUserServers(serversModal._id);
      fetchServerCounts(users);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Transfer failed');
    } finally { setSaving(false); }
  };

  if (loading) return <Layout><PageLoader /></Layout>;

  return (
    <Layout>
      <div className={`${admin.pageStretch} min-h-0`}>
        <div className="shrink-0">
          <h1 className={admin.title}>Users</h1>
          <p className={admin.subtitle}>Manage accounts, balances, and credentials.</p>
        </div>

        <div className={`${admin.tableShell} min-h-0 flex-1`}>
          <div className={admin.tableScroll}>
            <table className={admin.table}>
              <thead className={admin.thead}>
                <tr>
                  <th className={admin.th}>Username</th>
                  <th className={admin.th}>Email</th>
                  <th className={admin.th}>Balance</th>
                  <th className={admin.th}>Servers</th>
                  <th className={admin.th}>Role</th>
                  <th className={admin.th}>Created</th>
                  <th className={admin.th}>Actions</th>
                </tr>
              </thead>
              <tbody className={admin.tbody}>
                {pagedUsers.map((user) => (
                  <tr key={user._id} className={admin.tr}>
                    <td className={admin.td}>
                      <span className="font-medium text-white">{user.username}</span>
                    </td>
                    <td className={`${admin.td} ${admin.tdMuted}`}>{user.email}</td>
                    <td className={admin.td}>
                      <span className="font-semibold tabular-nums text-white">€{user.balance?.toFixed(2) || '0.00'}</span>
                    </td>
                    <td className={admin.td}>
                      <button type="button" onClick={() => openServersModal(user)} className="inline-flex items-center gap-1.5 text-[#7B9CFF] hover:text-white transition-colors text-sm font-medium" title="View servers">
                        <FiServer size={15} />
                        <span className="tabular-nums">{serverCounts[user._id] || 0}</span>
                      </button>
                    </td>
                    <td className={admin.td}>
                      <span className={roleBadgeClass(user.role)}>{user.role}</span>
                    </td>
                    <td className={`${admin.td} ${admin.tdMuted}`}>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td className={admin.td}>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => setEditingUser(editingUser === user._id ? null : user._id)} className={admin.btnIcon} title="Edit">
                          <FiEdit size={18} />
                        </button>
                        <button type="button" onClick={() => handleDelete(user._id)} className={admin.btnDanger} title="Delete">
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination page={page} pageSize={PAGE_SIZE} total={users.length} onPageChange={setPage} />
        </div>

        {/* Edit User Modal */}
        {editingUser && (
          <div className={admin.modalOverlay} onMouseDown={() => { setEditingUser(null); setBalanceAmount(''); setNewPassword(''); }}>
            <div className={admin.modalCard} onMouseDown={e => e.stopPropagation()}>
              <h2 className={admin.modalTitle}>Edit user</h2>
              <div className="space-y-5">
                <div>
                  <label className={admin.label}>Adjust balance</label>
                  <div className="flex gap-2">
                    <input type="number" step="0.01" value={balanceAmount} onChange={e => setBalanceAmount(e.target.value)} placeholder="Amount (+/-)" className={admin.input} />
                    <button type="button" onClick={() => handleBalanceUpdate(editingUser)} className={`${admin.btnPrimary} shrink-0 px-3`}><FiDollarSign size={18} /></button>
                  </div>
                </div>
                <div>
                  <label className={admin.label}>Set password</label>
                  <div className="flex gap-2">
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password" className={admin.input} />
                    <button type="button" onClick={() => handlePasswordUpdate(editingUser)} className={`${admin.btnPrimary} shrink-0 px-3`}><FiKey size={18} /></button>
                  </div>
                </div>
              </div>
              <button type="button" onClick={() => { setEditingUser(null); setBalanceAmount(''); setNewPassword(''); }} className={`${admin.btnSecondary} mt-6 w-full`}>Close</button>
            </div>
          </div>
        )}

        {/* User Servers Modal */}
        {serversModal && !transferModal && (
          <div className={admin.modalOverlay} onMouseDown={() => setServersModal(null)}>
            <div className="w-full max-w-2xl rounded-2xl border border-[#B8B8B847] bg-[#0f1428]/95 backdrop-blur-md p-6 shadow-[0_24px_50px_rgba(0,0,0,0.55)] max-h-[85vh] overflow-y-auto" onMouseDown={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className={admin.modalTitle + ' mb-0'}>{serversModal.username}'s Servers</h2>
                  <p className="text-sm text-white/50 mt-1">{userServers.length} server{userServers.length !== 1 ? 's' : ''}</p>
                </div>
                <button type="button" onClick={() => setServersModal(null)} className="text-white/50 hover:text-white"><FiX size={20} /></button>
              </div>

              {loadingServers ? (
                <div className="flex items-center justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-[#7B9CFF]" /></div>
              ) : userServers.length === 0 ? (
                <div className="text-center py-12 text-white/40">No servers found for this user.</div>
              ) : (
                <div className="space-y-3">
                  {userServers.map(s => (
                    <div key={s._id} className="flex items-center justify-between gap-3 rounded-xl border border-[#FFFFFF14] bg-[#FFFFFF06] p-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <FiServer className="shrink-0 text-[#8BA3FF]" size={16} />
                          <span className="font-medium text-white truncate">{s.name}</span>
                          <span className={statusBadgeClass(s.status)}>{s.status}</span>
                        </div>
                        <div className="text-xs text-white/40 flex flex-wrap gap-x-3 gap-y-1">
                          <span>Plan: {s.plan}</span>
                          <span>Location: {s.location}</span>
                          {s.ip && <span>IP: {s.ip}</span>}
                          <span>Expires: {new Date(s.expiresAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button type="button" onClick={() => openTransferFromUser(s)} className={admin.btnIcon} title="Transfer"><FiRepeat size={17} /></button>
                        <button type="button" onClick={() => handleDeleteServer(s._id)} className={admin.btnDanger} title="Delete"><FiTrash2 size={17} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transfer from User Servers Modal */}
        {transferModal && (
          <div className={admin.modalOverlay} onMouseDown={() => setTransferModal(null)}>
            <div className={admin.modalCard} onMouseDown={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h2 className={admin.modalTitle + ' mb-0'}>Transfer Server</h2>
                <button type="button" onClick={() => setTransferModal(null)} className="text-white/50 hover:text-white"><FiX size={20} /></button>
              </div>
              <p className="text-sm text-white/50 mb-4">Transfer <span className="text-white font-medium">{transferModal.name}</span> to another user.</p>
              <label className={admin.label}>Transfer to User</label>
              <input placeholder="Search users..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className={admin.input + ' mb-2'} />
              <select value={transferUserId} onChange={e => setTransferUserId(e.target.value)} className={admin.select + ' w-full'}>
                <option value="">-- Select User --</option>
                {filteredUsersForTransfer.map(u => <option key={u._id} value={u._id}>{u.username} ({u.email})</option>)}
              </select>
              <button type="button" disabled={saving} onClick={handleTransfer} className={`${admin.btnPrimary} mt-6 w-full`}>
                {saving ? 'Transferring...' : 'Transfer Server'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminUsers;
